// =====================================
// React Hook for Notification Service
// =====================================

import { useEffect, useState, useCallback } from 'react';
import { notificationService, NotificationMessage, NotificationServiceCallbacks } from '@/lib/api/notificationService';
import { useAuthStore } from '@/stores/authStore';

export interface NotificationState {
  isConnected: boolean;
  isAuthenticated: boolean;
  notifications: NotificationMessage[];
  unreadCount: number;
  error: string | null;
}

export interface UseNotificationsOptions {
  maxNotifications?: number;
  autoConnect?: boolean;
  showToast?: boolean;
  onNotification?: (notification: NotificationMessage) => void;
}

/**
 * React hook for managing real-time notifications
 */
export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const {
    maxNotifications = 100,
    autoConnect = true,
    showToast = true,
    onNotification
  } = options;

  const { user, accessToken: token } = useAuthStore();
  const [state, setState] = useState<NotificationState>({
    isConnected: false,
    isAuthenticated: false,
    notifications: [],
    unreadCount: 0,
    error: null,
  });

  // Add notification to state
  const addNotification = useCallback((notification: NotificationMessage) => {
    setState(prev => {
      const newNotifications = [notification, ...prev.notifications].slice(0, maxNotifications);
      return {
        ...prev,
        notifications: newNotifications,
        unreadCount: prev.unreadCount + 1,
      };
    });

    // Call custom notification handler
    onNotification?.(notification);

    // Show toast notification (if enabled and toast library is available)
    if (showToast && typeof window !== 'undefined') {
      // Would integrate with toast library like react-hot-toast or react-toastify
      console.log(`🔔 ${notification.title}: ${notification.message}`);
    }
  }, [maxNotifications, onNotification, showToast]);

  // Setup notification service callbacks
  const callbacks: NotificationServiceCallbacks = {
    onConnect: () => {
      setState(prev => ({ ...prev, isConnected: true, error: null }));
    },
    onDisconnect: () => {
      setState(prev => ({ ...prev, isConnected: false, isAuthenticated: false }));
    },
    onAuthSuccess: () => {
      setState(prev => ({ ...prev, isAuthenticated: true, error: null }));
    },
    onAuthError: (error) => {
      setState(prev => ({ ...prev, isAuthenticated: false, error }));
    },
    onNotification: addNotification,
    onOrderCreated: (data) => {
      addNotification({
        id: `order-${data.id || Date.now()}`,
        type: 'order_created',
        title: 'New Order',
        message: `Order #${data.orderNumber} created for table ${data.tableNumber}`,
        senderId: 'system',
        senderName: 'System',
        senderRole: 'system',
        restaurantId: user?.restaurantId || 'default',
        timestamp: new Date(),
        priority: 'high',
        data,
      });
    },
    onOrderReady: (data) => {
      addNotification({
        id: `order-ready-${data.id || Date.now()}`,
        type: 'order_ready',
        title: 'Order Ready',
        message: `Order #${data.orderNumber} for table ${data.tableNumber} is ready`,
        senderId: 'kitchen',
        senderName: 'Kitchen',
        senderRole: 'chef',
        restaurantId: user?.restaurantId || 'default',
        timestamp: new Date(),
        priority: 'urgent',
        data,
      });
    },
    onPaymentReceived: (data) => {
      addNotification({
        id: `payment-${data.paymentId || Date.now()}`,
        type: 'payment_received',
        title: 'Payment Received',
        message: `Payment of ${data.amount} ${data.currency} received for order #${data.orderNumber || data.orderId}`,
        senderId: 'system',
        senderName: 'Payment System',
        senderRole: 'system',
        restaurantId: user?.restaurantId || 'default',
        timestamp: new Date(),
        priority: 'normal',
        data,
      });
    },
    onTableStatusChanged: (data) => {
      if (data.newStatus === 'available') {
        addNotification({
          id: `table-${data.tableId}-${Date.now()}`,
          type: 'table_status_changed',
          title: 'Table Available',
          message: `Table ${data.tableNumber} is now available`,
          senderId: 'system',
          senderName: 'System',
          senderRole: 'system',
          restaurantId: user?.restaurantId || 'default',
          timestamp: new Date(),
          priority: 'low',
          data,
        });
      }
    },
    onKitchenUpdate: (data) => {
      addNotification({
        id: `kitchen-${Date.now()}`,
        type: 'kitchen_update',
        title: 'Kitchen Update',
        message: data.message,
        senderId: 'kitchen',
        senderName: 'Kitchen',
        senderRole: 'chef',
        restaurantId: user?.restaurantId || 'default',
        timestamp: new Date(),
        priority: data.priority || 'normal',
        data,
      });
    },
    onStaffAlert: (data) => {
      addNotification({
        id: `staff-alert-${Date.now()}`,
        type: 'staff_alert',
        title: data.title || 'Staff Alert',
        message: data.message,
        senderId: 'staff',
        senderName: 'Staff',
        senderRole: 'staff',
        restaurantId: user?.restaurantId || 'default',
        timestamp: new Date(),
        priority: data.priority || 'normal',
        data,
      });
    },
    onSystemAlert: (data) => {
      addNotification({
        id: `system-alert-${Date.now()}`,
        type: 'system_alert',
        title: 'System Alert',
        message: data.message || 'System notification',
        senderId: 'system',
        senderName: 'System',
        senderRole: 'system',
        restaurantId: user?.restaurantId || 'default',
        timestamp: new Date(),
        priority: data.priority || 'normal',
        data,
      });
    },
    onError: (error) => {
      setState(prev => ({ ...prev, error }));
      console.error('Notification service error:', error);
    },
  };

  // Connect to notification service when user is available
  useEffect(() => {
    if (autoConnect && user && token) {
      const userInfo = {
        userId: user.id,
        name: user.name || user.loginName || user.login,
        role: user.role,
        restaurantId: user.restaurantId || 'default',
      };

      notificationService.connect(token, userInfo, callbacks);
    }

    return () => {
      notificationService.disconnect();
    };
  }, [user, token, autoConnect]);

  // Actions
  const connect = useCallback(() => {
    if (user && token) {
      const userInfo = {
        userId: user.id,
        name: user.name || user.loginName || user.login,
        role: user.role,
        restaurantId: user.restaurantId || 'default',
      };
      notificationService.connect(token, userInfo, callbacks);
    }
  }, [user, token, callbacks]);

  const disconnect = useCallback(() => {
    notificationService.disconnect();
  }, []);

  const clearNotifications = useCallback(() => {
    setState(prev => ({
      ...prev,
      notifications: [],
      unreadCount: 0,
    }));
  }, []);

  const markAsRead = useCallback((notificationId?: string) => {
    setState(prev => {
      if (notificationId) {
        // Mark specific notification as read (if needed for future implementation)
        return prev;
      } else {
        // Mark all as read
        return {
          ...prev,
          unreadCount: 0,
        };
      }
    });
  }, []);

  const removeNotification = useCallback((notificationId: string) => {
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.filter(n => n.id !== notificationId),
      unreadCount: Math.max(0, prev.unreadCount - 1),
    }));
  }, []);

  // Notification service actions
  const sendOrderCreatedNotification = useCallback((orderData: any) => {
    notificationService.notifyOrderCreated(orderData);
  }, []);

  const sendOrderReadyNotification = useCallback((orderData: any) => {
    notificationService.notifyOrderReady(orderData);
  }, []);

  const sendPaymentNotification = useCallback((paymentData: any) => {
    notificationService.notifyPaymentReceived(paymentData);
  }, []);

  const sendTableStatusNotification = useCallback((tableData: any) => {
    notificationService.notifyTableStatusChanged(tableData);
  }, []);

  const sendKitchenUpdateNotification = useCallback((updateData: any) => {
    notificationService.notifyKitchenUpdate(updateData);
  }, []);

  const sendStaffAlert = useCallback((alertData: any) => {
    notificationService.notifyStaffAlert(alertData);
  }, []);

  const sendCustomNotification = useCallback((notificationData: any) => {
    notificationService.sendNotification(notificationData);
  }, []);

  const ping = useCallback(() => {
    notificationService.ping();
  }, []);

  const getConnectionInfo = useCallback(() => {
    return notificationService.getConnectionInfo();
  }, []);

  const enableDebug = useCallback(() => {
    notificationService.enableDebug();
  }, []);

  const disableDebug = useCallback(() => {
    notificationService.disableDebug();
  }, []);

  return {
    // State
    ...state,
    
    // Actions
    connect,
    disconnect,
    clearNotifications,
    markAsRead,
    removeNotification,
    
    // Notification sending
    sendOrderCreatedNotification,
    sendOrderReadyNotification,
    sendPaymentNotification,
    sendTableStatusNotification,
    sendKitchenUpdateNotification,
    sendStaffAlert,
    sendCustomNotification,
    
    // Utility
    ping,
    getConnectionInfo,
    enableDebug,
    disableDebug,
  };
};